import { Cancel, CheckCircleOutlined, Flaky } from '@mui/icons-material';
import { Box, Button, Divider } from '@mui/material';
import { BadgeProps } from '@mui/material/Badge/Badge';
import { SvgIconProps } from '@mui/material/SvgIcon/SvgIcon';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ProcessAgent,
  ProcessItem,
  ProcessItemBadge,
  ProcessItemProps,
} from '~/components/WorkflowProposalProcess/components';
import { useProcessApprovalStep } from '~/components/WorkflowProposalProcess/components/ProcessApprovalStep/useProcessApprovalStep';
import { getProcessStepApprovalStatusColor } from '~/components/WorkflowProposalProcess/data';
import {
  ApprovalStepState,
  ApprovalStepStatus,
  ProposalAgent,
  getApprovalStepStatusTranslationKey,
} from '~/data/proposals';

import { useSxStyles } from './useStyles';

type PropsType = {
  onDecision: (stepSeq: number, newStatus: ApprovalStepStatus) => void;
  loggedUserAgency?: ProposalAgent;
  step: ApprovalStepState;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  substituteAsUltimateApprover?: boolean;
  isPreview?: boolean;
  isConcluded?: boolean;
};

export const ProcessApprovalStep = (props: PropsType) => {
  const {
    step,
    isFirstStep,
    isLastStep,
    loggedUserAgency,
    onDecision,
    substituteAsUltimateApprover,
    isPreview,
    isConcluded,
  } = props;
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const { state, userCanDecide, showApprovers } = useProcessApprovalStep({
    step,
    loggedUserAgency,
    substituteAsUltimateApprover,
  });

  const handleDecision = (decision: ApprovalStepStatus) => {
    onDecision(step.seq, decision);
  };

  const isDisabled =
    isPreview || (isConcluded && step.state === ApprovalStepStatus.PENDING);

  const renderStateIcon = (status: ApprovalStepStatus): ReactNode => {
    const color = (
      isDisabled ? 'disabled' : getProcessStepApprovalStatusColor(status)
    ) as SvgIconProps['color'];

    switch (status) {
      case ApprovalStepStatus.APPROVED:
        return <CheckCircleOutlined color={color} />;
      case ApprovalStepStatus.PENDING:
        return <Flaky color={color} />;
      case ApprovalStepStatus.REJECTED:
        return <Cancel color={color} />;
    }
  };

  const DecisionButtons = () => {
    return !isDisabled ? (
      <>
        {userCanDecide.asSubstitute && (
          <Divider>{t('decideOnBehalfOf')}</Divider>
        )}
        {(userCanDecide.asRequested || userCanDecide.asSubstitute) && (
          <Box sx={sxStyles.approveStep}>
            <Button
              color={'success'}
              startIcon={<CheckCircleOutlined />}
              onClick={() => handleDecision(ApprovalStepStatus.APPROVED)}
            >
              {t('approve')}
            </Button>
            <Button
              color={'error'}
              startIcon={<Cancel />}
              onClick={() => handleDecision(ApprovalStepStatus.REJECTED)}
            >
              {t('reject')}
            </Button>
          </Box>
        )}
      </>
    ) : (
      <></>
    );
  };

  return (
    <ProcessItem
      itemBadge={
        <ProcessItemBadge
          color={
            getProcessStepApprovalStatusColor(step.state) as BadgeProps['color']
          }
          badgeContent={step.seq}
          disabled={isDisabled}
        >
          {renderStateIcon(step.state)}
        </ProcessItemBadge>
      }
      isFirstItem={isFirstStep}
      isLastItem={isLastStep}
      status={t(getApprovalStepStatusTranslationKey(step.state))}
      statusColor={
        (isDisabled
          ? 'disabled'
          : getProcessStepApprovalStatusColor(
              step.state,
            )) as ProcessItemProps['statusColor']
      }
      timestamp={state.isUpdated ? step.updatedByTimestamp : undefined}
    >
      <ProcessAgent agent={step.updatedBy} />
      {state.decisionBySubstitute && (
        <Divider>{t('decidedOnBehalfOf')}</Divider>
      )}
      {showApprovers &&
        step.approvers
          .filter((agent) => agent.userId !== step.updatedBy.userId)
          .map((agent, index) => (
            <ProcessAgent agent={agent} key={`agent-${index}`} />
          ))}
      <DecisionButtons />
    </ProcessItem>
  );
};
